import { Component, inject, Input, input, OnChanges, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {
  BottomNavClearButtonsList,
  BottomNavService,
  BottomNavUpdateButtonsList,
  BottomNavUpdateButtonState,
  BottomNavUpdateState,
} from '../../../../shared/bottom-nav/bottom-nav.service';
import { ButtonStyles, ButtonTypes } from '../../../../button/button.component';
import { ZiroFormAbstractDirective } from '../../../../forms/ziro-form-abstract.directive';
import { ZiroFormsUpdate, ZiroFormsValidationState } from '../../../../forms/ziro-forms-models';
import { ZiroIcons } from '../../../../shared/models/ziro-icons.enum';
import { combineLatest, Observable, of, Subscription, switchMap, tap, timer } from 'rxjs';
import { ZiroFormStateService } from '../../../../forms/ziro-form-state.service';
import {
  WebexCallingSettings,
  WebexCallingStatus,
  WebexCallingStatusEnum,
} from '../../../../shared/models/generated/ziroModels';
import { HtmlInputType, ZiroTextComponent, ZiroTextConfig } from '../../../../forms/fields/text/ziro-text.component';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { ZiroModalService } from '../../../../shared/services/ziro-modal.service';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from '../../../../shared/services/toast.service';
import { ToastTypes } from '../../../../shared/services/abstract/toast.service.abstract';
import { WebexCallingSettingsContext } from '../../../contexts/webex-calling-settings.context';
import { WebexCallingStatusContext } from '../../../contexts/webex-calling-status.context';
import moment from 'moment';

@Component({
  selector: 'ziro-webex-authentication-form',
  templateUrl: './webex-authentication-form.component.html',
  styleUrls: ['./webex-authentication-form.component.scss'],
  standalone: false,
})
export class WebexAuthenticationFormComponent
  extends ZiroFormAbstractDirective<WebexCallingSettings>
  implements OnInit, OnChanges, OnDestroy
{
  protected _ziroFormStateService: ZiroFormStateService;
  private _bottomNavService = inject(BottomNavService);
  private _ziroModalService = inject(ZiroModalService);
  private _translateService = inject(TranslateService);
  private _toastService = inject(ToastService);
  private _webexCallingSettingsContext = inject(WebexCallingSettingsContext);
  private _webexCallingStatusContext = inject(WebexCallingStatusContext);

  @ViewChild('clientSecret') clientSecret: ZiroTextComponent;
  @ViewChild('tooltip') tooltip: NgbTooltip;
  // TODO: Skipped for migration because:
  //  Your application code writes to the input. This prevents migration.
  @Input() clientIdExists: boolean;
  isRefreshing = false;
  ziroIcons = ZiroIcons;
  buttonStyles = ButtonStyles;
  readonly callingSettings = input<WebexCallingSettings>();
  callingStatus: WebexCallingStatus;
  WebexCallingStatusEnum = WebexCallingStatusEnum;
  formattedExpirationDate: string;
  isLoading = true;
  formConfig = {
    fields: {
      clientId: {
        label: 'tkey;admin.webex_authentication.client_id.label',
        dataAutomation: 'client-id',
        required: () => true,
        disabled: () => this.clientIdExists,
        disabledTooltip: 'tkey;admin.webex_authentication.client_already_provided.disabled_tooltip',
        componentConfig: new ZiroTextConfig({ htmlInputType: HtmlInputType.TEXT }),
      },
      clientSecret: {
        label: 'tkey;admin.webex_authentication.client_secret.label',
        dataAutomation: 'client-secret',
        required: () => !this.isExisting,
        disabled: () => this.clientIdExists,
        disabledTooltip: 'tkey;admin.webex_authentication.client_already_provided.disabled_tooltip',
        componentConfig: new ZiroTextConfig({ htmlInputType: HtmlInputType.PASSWORD }),
      },
      redirectUri: {
        label: 'tkey;admin.webex_authentication.redirect_uri.label',
        dataAutomation: 'redirect-uri',
        disabled: () => true,
        helpText: 'tkey;admin.webex_authentication.redirect_uri.help_text',
        componentConfig: new ZiroTextConfig({ htmlInputType: HtmlInputType.TEXT }),
      },
    },
  };
  private _subscriptions = new Subscription();
  private _validationState = ZiroFormsValidationState.VALID;
  private readonly SCOPE =
    'spark:people_read spark:people_write spark-admin:licenses_read spark-admin:people_read spark-admin:people_write spark-admin:telephony_config_read spark-admin:telephony_config_write spark-admin:devices_read spark-admin:devices_write';

  ngOnInit(): void {
    const callingStatusSub = this._webexCallingStatusContext.state$.subscribe((callingStatus: WebexCallingStatus) => {
      this.callingStatus = callingStatus;
      this._setBottomNav();
      this.formatExpirationDate(this.callingStatus.accessTokenExpirationDate);
      this.isLoading = false;
    });

    const formUpdateSub = this.ziroFormsUpdate$.subscribe((updates: ZiroFormsUpdate<WebexCallingSettings>) => {
      this._validationState = updates.valid;
    });

    this._subscriptions.add(callingStatusSub);
    this._subscriptions.add(formUpdateSub);
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
    this._bottomNavService.dispatch(new BottomNavClearButtonsList());
  }

  onCopyUriClick(t: NgbTooltip): void {
    timer(1000).subscribe(() => {
      t.close();
    });
  }

  redirectToWebex(): void {
    window.location.href = `https://webexapis.com/v1/authorize?client_id=${encodeURIComponent(
      this.callingSettings().clientId,
    )}&response_type=code&redirect_uri=${encodeURIComponent(
      this.callingSettings().redirectUri,
    )}&scope=${encodeURIComponent(this.SCOPE)}&state=set_state_here`;
  }

  onRefreshTokensClick(): void {
    this.isRefreshing = true;

    this._webexCallingStatusContext.refreshWebexCallingStatusTokens().subscribe(() => {
      this.isRefreshing = false;
      this._toastService.push(
        ToastTypes.SUCCESS,
        ZiroIcons.SAVE_SUCCESS,
        'tkey;shared.toast.refresh.success.title',
        'tkey;admin.webex_authentication.toast.token_refresh_success',
      );
    });
  }

  formatExpirationDate(dateString: string): void {
    const dateUtc = new Date(dateString).toUTCString();
    this.formattedExpirationDate = moment(dateUtc).format('MMMM Do YYYY');
  }

  protected submit(): Observable<null> {
    this._bottomNavService.dispatch(new BottomNavUpdateState({ hasValidationError: !this.isFormValid() }));
    this._setBottomNavButtonsState('save');
    return this._webexCallingSettingsContext.updateCallingSettings(this.entity).pipe(
      switchMap(() => {
        return this._webexCallingSettingsContext.refreshCallingSettings().pipe(
          switchMap(() => {
            this.setIsExisting(true);
            this._toastService.push(
              ToastTypes.SUCCESS,
              ZiroIcons.SAVE_SUCCESS,
              'tkey;shared.toast.save.success.title',
              'tkey;admin.webex_authentication.toast.save',
            );
            this.clientIdExists = true;
            this.callingSettings().clientId = this.formData.clientId;
            this._setBottomNav();
            return of(null);
          }),
        );
      }),
    );
  }

  private _setBottomNavButtonsState(type: 'save' | 'delete' | null): void {
    this._bottomNavService.dispatch(
      new BottomNavUpdateButtonState({
        id: 'bottom-nav-delete-button',
        state: {
          pending: type === 'delete',
          buttonDisableState: { disabled: type !== null, tooltipKey: '' },
        },
      }),
    );
    this._bottomNavService.dispatch(
      new BottomNavUpdateButtonState({
        id: 'bottom-nav-save-button',
        state: {
          pending: type === 'save',
          buttonDisableState: { disabled: type !== null, tooltipKey: '' },
        },
      }),
    );
  }

  private _setBottomNav: () => void = () => {
    if (this.callingSettings().clientId) {
      this._bottomNavService.dispatch(
        new BottomNavUpdateButtonsList([
          {
            id: 'bottom-nav-delete-button',
            dataAutomation: 'bottom-nav-delete-button',
            label: 'tkey;global.button.delete.text',
            icon: this.ziroIcons.DELETE_SMALL,
            buttonClass: ButtonStyles.DANGER,
            cb: () => this._onDeleteClicked(),
          },
          {
            id: 'bottom-nav-save-button',
            dataAutomation: 'bottom-nav-save-button',
            label: 'tkey;global.button.save.text',
            icon: this.ziroIcons.SAVE_SUCCESS,
            buttonClass: ButtonStyles.PRIMARY,
            type: ButtonTypes.SUBMIT,
            submitSubject: this._validateAndSubmitSource,
            state: {
              pending: false,
              buttonDisableState: {
                disabled: true,
                tooltipKey: 'tkey;admin.webex_authentication.client_already_provided.disabled_tooltip',
              },
              tooltipVisible: true,
            },
          },
        ]),
      );
    } else {
      this._bottomNavService.dispatch(
        new BottomNavUpdateButtonsList([
          {
            id: 'bottom-nav-save-button',
            dataAutomation: 'bottom-nav-save-button',
            label: 'tkey;global.button.save.text',
            icon: this.ziroIcons.SAVE_SUCCESS,
            buttonClass: ButtonStyles.PRIMARY,
            type: ButtonTypes.SUBMIT,
            submitSubject: this._validateAndSubmitSource,
          },
        ]),
      );
    }

    const formSubmittedSub = this._validateAndSubmitSource.subscribe(() => {
      this._bottomNavService.dispatch(
        new BottomNavUpdateState({
          hasValidationError: !this.isFormValid(),
        }),
      );
    });
    this._subscriptions.add(formSubmittedSub);
  };

  private _onDeleteClicked(): void {
    const options = {
      windowClass: 'delete-button-modal',
      modalViewProperties: {
        icon: ZiroIcons.DELETE_BIG,
        iconClass: 'text-danger',
        promptBody: this._translateService.instant('tkey;admin.webex_authentication.delete_modal'),
        displayCloseButton: true,
        buttons: [
          {
            label: 'tkey;dialogs.button.cancel',
            buttonClass: ButtonStyles.OUTLINE_DEFAULT,
            dataAutomation: 'confirmation-modal-cancel-button',
          },
          {
            label: 'tkey;dialogs.button.delete',
            buttonClass: ButtonStyles.OUTLINE_DANGER,
            dataAutomation: 'confirmation-modal-confirm-button',
            cb: () => {
              return this._deleteWebexCalling();
            },
          },
        ],
      },
    };

    this._ziroModalService.openPromptModal(() => options.modalViewProperties, options);
  }

  private _deleteWebexCalling(): Observable<readonly unknown[]> {
    this._setBottomNavButtonsState('delete');
    return this._webexCallingSettingsContext.deleteCallingSettings().pipe(
      tap(() => {
        this.setIsExisting(false);
        this._setIsFormSubmitted(false);
        this._ziroFormStateService.setIsFormDirty(false);
        this.clientSecret.showMaskedInput = false;
        this._toastService.push(
          ToastTypes.INFO,
          `${ZiroIcons.DELETE_SMALL} text-danger`,
          'tkey;shared.toast.delete.success.title',
          'tkey;admin.webex_authentication.toast.delete',
        );
        this.callingSettings().clientId = '';
        this.clientIdExists = false;
        this._setBottomNav();
      }),
      switchMap(() =>
        combineLatest([
          this._webexCallingSettingsContext.refreshCallingSettings(),
          this._webexCallingStatusContext.refreshWebexCallingStatus(),
        ]),
      ),
    );
  }
}
